import { fetcher, handleErrors } from "./fetch";

// Every 401 means the server never got past sign-in (Api::BaseController answers { error: "Invalid Request" }), so
// nothing was changed or sent to a carrier. Staff see this instead of "Invalid Request" or "401: Unauthorized".
export const SESSION_EXPIRED = "Your session expired. Sign in again.";
const CLEAR_REFUSED = "Only a manager or admin can clear a stuck booking.";

// The 401 body's error, or null for a bare 401 (head :unauthorized).
const errorOf401 = async (response) => {
  try {
    const body = await response.clone().json();
    return (body && body.error) || null;
  } catch (e) {
    return null;
  }
};

// handleErrors with 401 mapped. bare401 is the message for a bare 401 on a route whose role guard answers one
// (sign-in already passed there, so a bare 401 is a refusal, not an expired session).
const checked = async (response, { bare401 = null } = {}) => {
  if (response.status === 401) {
    if (bare401 && !(await errorOf401(response))) throw new Error(bare401);
    throw new Error(SESSION_EXPIRED);
  }
  return handleErrors(response);
};

const send = (method, url, body) =>
  fetcher(url, {
    method,
    mode: "cors",
    cache: "no-cache",
    credentials: "omit",
    headers: { "Content-Type": "application/json" },
    redirect: "follow",
    referrerPolicy: "no-referrer",
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
const postData = async (url, body, options) => checked(await send("POST", url, body), options);
const putData = async (url, body) => checked(await send("PUT", url, body));
const deleteRequest = async (url) => checked(await send("DELETE", url));
const get = async (url) => checked(await fetcher(url));

export const fetchManualDeliveries = async (shopId, startDate, endDate) => {
  const params = new URLSearchParams();
  if (startDate) params.append("start_date", startDate);
  if (endDate) params.append("end_date", endDate);

  const response = await get(
    `/api/shops/${shopId}/manual_deliveries?${params.toString()}`
  );
  return response.json();
};

export const updateManualDelivery = async (
  shopId,
  deliveryId,
  deliveryData
) => {
  const response = await putData(
    `/api/shops/${shopId}/manual_deliveries/${deliveryId}`,
    {
      manual_delivery: deliveryData,
    }
  );
  return response.json();
};

export const deleteManualDelivery = async (shopId, deliveryId) => {
  return deleteRequest(`/api/shops/${shopId}/manual_deliveries/${deliveryId}`);
};

// Booking calls return { ok, status, data } instead of throwing: 409 (price changed, data.quote is the fresh
// quote), 422 (carrier refused, the row is kept pending) and 423 (outcome unknown) all carry data the panel needs.
// A 401 comes back as data.error SESSION_EXPIRED: nothing reached the carrier. (postData throws on non-2xx, so
// these use fetcher directly.)
const postForResult = async (url, body) => {
  const response = await fetcher(url, {
    method: "POST",
    mode: "cors",
    credentials: "omit",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (response.status === 401) {
    return { ok: false, status: 401, data: { error: SESSION_EXPIRED } };
  }
  let data = {};
  try {
    data = await response.json();
  } catch (e) {
    data = {};
  }
  return { ok: response.ok, status: response.status, data };
};

export const bookManualDelivery = (shopId, payload) =>
  postForResult(`/api/shops/${shopId}/manual_deliveries`, {
    manual_delivery: payload,
  });

// The shop's zone and today's date there ({ timezone, today }); every wall-clock time on the page is shown in this
// zone.
export const fetchManualDeliveryContext = async (shopId) => {
  const response = await get(`/api/shops/${shopId}/manual_deliveries/context`);
  return response.json();
};

// Prices one shipment with the own driver and every enabled carrier. 200 data.options; 422 data.error for an
// incomplete shipment (no carrier was asked).
export const quoteAllOptions = (shopId, shipment) =>
  postForResult(`/api/shops/${shopId}/manual_deliveries/quotes`, {
    manual_delivery: { shipment },
  });

export const createOwnDriverDelivery = (shopId, payload) =>
  postForResult(`/api/shops/${shopId}/manual_deliveries`, {
    manual_delivery: payload,
  });

// Retries a pending row that the server kept after a failed booking. confirmedPriceCents is the carrier price
// staff accepted; the server books only at that price (or at a stored price they already confirmed) and
// answers 409 with the fresh quote otherwise.
export const retryManualDeliveryBooking = (shopId, id, confirmedPriceCents) =>
  postForResult(
    `/api/shops/${shopId}/manual_deliveries/${id}/schedule`,
    Number.isInteger(confirmedPriceCents)
      ? { confirmed_price_cents: confirmedPriceCents }
      : {}
  );

// Manager/admin only; the caller must have told staff to check the carrier first.
export const clearManualDeliveryAttempt = async (shopId, id) => {
  const response = await postData(
    `/api/shops/${shopId}/manual_deliveries/${id}/clear_booking_attempt`,
    { confirm_carrier_checked: true },
    { bare401: CLEAR_REFUSED }
  );
  return response.json();
};

const downloadBlob = (blob, filename, type) => {
  const url = window.URL.createObjectURL(new Blob([blob], { type }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

export const openManualDeliveryLabel = async (shopId, id) => {
  const response = await get(`/api/shops/${shopId}/manual_deliveries/${id}/label`);
  downloadBlob(
    await response.blob(),
    `manual-delivery-${id}-label.pdf`,
    "application/pdf"
  );
};

// Downloads exactly the rows on screen, in screen order. -> the file name.
export const exportManualDeliveriesCsv = async (shopId, ids, todayIso) => {
  const response = await postData(`/api/shops/${shopId}/manual_deliveries/export`, { ids });
  const filename = `manual-deliveries-${todayIso}.csv`;
  downloadBlob(await response.blob(), filename, "text/csv");
  return filename;
};
